import React, { useRef } from "react";
import { motion, useInView } from 'framer-motion';
import WHAT_BUILT_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/whatwebuilt_lpatop.webp';
import COIN3_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/coin3.png';
import COIN4_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/coin4.png';

type CryoneSolutionsProps = {
    cryoneProject: any
}

const CryoneSolutions = ({ cryoneProject }: CryoneSolutionsProps) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: false, amount: 0.2 })

    const fadeUp = {
        hidden: { opacity: 0, y: 34 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] } }
    }

    const staggerCards = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
    }

    const cardItem = {
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.55 } }
    }

    const { cryoneProjectDetails } = cryoneProject ?? {}
    const { whatWeBuiltSection } = cryoneProjectDetails ?? {}
    const buildItems = whatWeBuiltSection?.items

    return (
        <section className="relative isolate w-full bg-[#141414] py-16 sm:py-20" style={{ overflow: 'visible' }} ref={sectionRef}>
            <div
                aria-hidden="true"
                className="pointer-events-none absolute left-[-140px] top-[18%] h-[320px] w-[320px] rounded-[42%_58%_55%_45%/40%_48%_52%_60%] bg-[#B41F1F]/35 blur-[70px]"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute right-[-150px] bottom-[12%] h-[340px] w-[340px] rounded-[56%_44%_40%_60%/46%_56%_44%_54%] bg-[#B41F1F]/30 blur-[78px]"
            />

            <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6">

                {/* Title */}
                <motion.div
                    className="flex items-center justify-center gap-4"
                    variants={fadeUp}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    <span className="w-3 h-3 rounded-full bg-[#B41F1F]" />
                    <h2 className="uppercase font-extrabold tracking-[1.5px] text-[35px] sm:text-[46px] md:text-[75px] text-white text-center">
                        {whatWeBuiltSection?.title}
                    </h2>
                    <span className="w-3 h-3 rounded-full bg-[#B41F1F]" />
                </motion.div>

                {/* Laptop image + floating coins — full viewport width strip */}
                <div
                    className="relative mt-10 flex justify-center items-center"
                    style={{
                        /* Break out of the container so coins can reach viewport edges */
                        width: '100vw',
                        marginLeft: 'calc(-50vw + 50%)',
                        overflow: 'visible',
                    }}
                >
                    {/* Main laptop mockup */}
                    <motion.img
                        src={WHAT_BUILT_IMAGE}
                        alt="Cryone platform mockup"
                        className="w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.55)]"
                        style={{ maxWidth: '1700px' }}
                        variants={fadeUp}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                        whileHover={{ y: -4, transition: { duration: 0.25 } }}
                    />

                    {/* Coin 3 — left side, overlapping left laptop edge */}
                    <motion.img
                        src={COIN3_IMAGE}
                        alt="Cryone coin 3"
                        className="pointer-events-none absolute z-10 w-[60px] sm:w-[110px] md:w-[150px]"
                        style={{
                            /* Position it over the left side of the laptop */
                            left: '15%',
                            top: '0%',
                            transform: 'translateY(-50%)',
                        }}
                        animate={isInView ? { y: [0, -14, 0], rotate: [0, -9, 0] } : {}}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    {/* Coin 4 — right side, fully outside right laptop edge */}
                    <motion.img
                        src={COIN4_IMAGE}
                        alt="Cryone coin 4"
                        className="pointer-events-none absolute z-10 w-[60px] sm:w-[110px] md:w-[150px]"
                        style={{
                            right: '0%',
                            top: '20%',
                            transform: 'translateY(-50%)',
                        }}
                        animate={isInView ? { y: [0, 14, 0], rotate: [0, 9, 0] } : {}}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    />
                </div>

                {/* Description text */}
                <motion.p
                    className="mt-8 sm:mt-10 text-center text-white/90 text-[14px] sm:text-[22px] md:text-[32px] leading-[1.4] max-w-[360px] sm:max-w-[900px] mx-auto"
                    variants={fadeUp}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    {whatWeBuiltSection?.intro}
                </motion.p>

                {/* Feature cards */}
                <motion.div
                    className="mt-8 sm:mt-10 grid grid-cols-2 md:grid-cols-2 gap-4 sm:gap-7 max-w-[950px] mx-auto"
                    variants={staggerCards}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    {buildItems?.map((item: { key: string; title: string }) => (
                        <motion.div
                            key={item.key}
                            className="rounded-[18px] sm:rounded-[22px] border border-[#B41F1F] bg-[#141414] px-4 py-4 sm:px-8 sm:py-8 md:px-10 md:py-10"
                            variants={cardItem}
                            whileHover={{ y: -4, transition: { duration: 0.2 } }}
                        >
                            <p className="text-[#EF4444] font-extrabold text-[32px] sm:text-[52px] leading-none">
                                {item.key}
                            </p>
                            <p className="mt-3 sm:mt-5 text-white text-[12px] sm:text-[20px] md:text-[24px] leading-[1.35]">
                                {item.title}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Footer text */}
                <motion.p
                    className="mt-10 sm:mt-12 text-center text-white text-[14px] sm:text-[22px] md:text-[30px] leading-[1.35] max-w-[360px] sm:max-w-[900px] mx-auto"
                    variants={fadeUp}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    {whatWeBuiltSection?.footer}
                </motion.p>
            </div>
        </section>
    );
};

export default CryoneSolutions;
